/**
 * AbsenceCheckbox Component
 *
 * Shared absence toggle checkbox.
 * Used by AddForm and EditForm for hard bookings.
 *
 * @example
 * ```tsx
 * <AbsenceCheckbox
 *   checked={state.isAbsence}
 *   onChange={onAbsenceToggle}
 *   testIdPrefix="add-form"
 * />
 * ```
 */

import React from "react";
import classNames from "classnames";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { generateId } from "@/lib/utils";
import styles from "./AbsenceCheckbox.module.css";

export interface AbsenceCheckboxProps {
  /** Whether absence is checked */
  checked: boolean;
  /** Change handler */
  onChange: () => void;
  /** Prefix for test ID (e.g., "add-form" or "edit-form") */
  testIdPrefix: string;
  /** Label text */
  label?: string;
  /** Optional additional CSS class */
  className?: string;
}

export const AbsenceCheckbox: React.FC<AbsenceCheckboxProps> = ({
  checked,
  onChange,
  testIdPrefix,
  label = "Fravær",
  className,
}) => {
  return (
    <div className={classNames(styles.absenceCheckbox, className)}>
      <Checkbox
        id={generateId(`${testIdPrefix}-absence`)}
        checked={checked}
        onChange={onChange}
        label={label}
        data-testid={`${testIdPrefix}-absence`}
      />
    </div>
  );
};

AbsenceCheckbox.displayName = "AbsenceCheckbox";

export default AbsenceCheckbox;
